import { FormEvent, useState } from "react";
import { Button } from "../../elements/Button";
import { CancelButton } from "../../elements/CancelButton";
import { Modal } from "../../elements/Modal";
import { CheckboxField } from "../fields/CheckboxField";
import { EmailField } from "../fields/EmailField";
import { LookupField } from "../fields/LookupField";
import { PicklistField } from "../fields/PicklistField";
import { TextField } from "../fields/TextField";
import { LinkKind, LinkKindOptions, LinkValues, PredefinedLinks, validateLink } from "./linkHref";
import { LinkDialogProps } from "./RichTextDialogs.types";
import { useEntityAttributes } from "./useEntityAttributes";

/** Makes or changes a link: where it goes, what it says, and whether it opens a new tab. */
export function RichTextLinkDialog({ initial, entities, onRemove, onApply, onClose }: LinkDialogProps) {
  const [link, setLink] = useState<LinkValues>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof LinkValues, string>>>({});
  const { options: attributes } = useEntityAttributes(
    link.kind === "attribute" ? entities[0]?.entityName ?? null : null
  );
  const kinds = LinkKindOptions.filter((kind) => kind.Key !== "attribute" || entities.length > 0);

  const set = <Key extends keyof LinkValues>(key: Key) => (value: LinkValues[Key]) =>
    setLink((current) => ({ ...current, [key]: value }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const found = validateLink(link);

    setErrors(found);

    if (Object.keys(found).length === 0) {
      onApply(link);
    }
  };

  return (
    <Modal title="Link" onClose={onClose}>
      <form onSubmit={submit} noValidate>
        <div className="modal-body">
          <PicklistField
            label="Type"
            name="rt-link-kind"
            value={link.kind}
            options={kinds}
            hidePlaceholder
            onChange={(value) => set("kind")((value ?? "web") as LinkKind)}
          />
          {link.kind === "web" && (
            <TextField label="URL" name="rt-link-url" value={link.url} error={errors.url} onChange={set("url")} />
          )}
          {link.kind === "email" && (
            <>
              <EmailField
                label="Email Address"
                name="rt-link-email"
                value={link.emailAddress}
                error={errors.emailAddress}
                onChange={set("emailAddress")}
              />
              <TextField label="Email Subject" name="rt-link-subject" value={link.emailSubject} onChange={set("emailSubject")} />
              <TextField label="Email Content" name="rt-link-content" value={link.emailContent} onChange={set("emailContent")} />
            </>
          )}
          {link.kind === "predefined" && (
            <PicklistField
              label="Link"
              name="rt-link-predefined"
              value={link.predefined}
              options={PredefinedLinks}
              error={errors.predefined}
              onChange={(value) => set("predefined")(value ?? "")}
            />
          )}
          {link.kind === "attribute" && (
            <LookupField
              label="Attribute"
              name="rt-link-attribute"
              value={link.attribute}
              options={attributes}
              searchable
              error={errors.attribute}
              onChange={set("attribute")}
            />
          )}
          <TextField label="Text" name="rt-link-text" value={link.text} error={errors.text} onChange={set("text")} />
          <CheckboxField label="Open link in new tab" name="rt-link-newtab" value={link.newTab} onChange={set("newTab")} />
        </div>
        <div className="modal-footer">
          {onRemove && <Button variant="outline-danger" text="Remove link" className="me-auto" onClick={onRemove} />}
          <CancelButton onClick={onClose} />
          <Button variant="primary" type="submit" text="Confirm" />
        </div>
      </form>
    </Modal>
  );
}
